import React, { useContext, useEffect } from 'react'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Callout,
  Box as Card,
  Box as Container,
  Box as DetailsContainer,
  Highlight,
  Text,
} from '@node-space/ui-components'
import { RequestErrorCallout } from 'components/Errors/RequestErrorCallout'
import { useMinutesSecondsTimer } from 'hooks/useMinutesSecondsTimer'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { floorWithPrecision, numberWithCommas } from 'utils/utils'
import { convertFromWalletAtom, convertToWalletAtom, quoteAtom } from './atoms/atoms'
import { useConvertReview } from './hooks/useConvertReview'

export const ConvertReview = () => {
  const { t } = useTranslation()
  const convertToWallet = useAtomValue(convertToWalletAtom)
  const convertFromWallet = useAtomValue(convertFromWalletAtom)
  const quote = useAtomValue(quoteAtom)
  const [walletAction] = useContext(WalletActionContext)
  const {
    requestError,
    isRefreshingQuote,
    isAcceptingQuote,
    handleAcceptQuote,
    handleRefreshQuote,
  } = useConvertReview()

  return (
    <Container width="full" data-testid="review-container">
      {requestError?.show && (
        <Box paddingT={4} paddingB={16}>
          <RequestErrorCallout requestError={requestError} />
        </Box>
      )}
      <Card background="subtle" padding={16}>
        <DetailsContainer flexCol align="center" gapY={8}>
          <Box flexCol paddingT={4} gapY={8} width="full">
            <Box flex justify="between" gapX={16}>
              <Text color="subdued">{t('wallets.convert.youWillReceive')}</Text>
              <Box flex gapX={4} align="center" className="text-end">
                <Text variant="body-label">{`${quote?.amountOut} ${quote?.to}`}</Text>
              </Box>
            </Box>
          </Box>
          <Box flexCol paddingT={4} gapY={8} width="full">
            <Box flex justify="between" gapX={16}>
              <Text color="subdued">
                {t('wallets.convert.currencyPrice', { currency: convertToWallet?.currency?.code })}
              </Text>
              <Box className="text-end">
                <Text variant="body-label">{`1 ${convertToWallet?.currency?.code} = ${floorWithPrecision(
                  quote?.price,
                  convertFromWallet?.currency?.pricePrecision
                )} ${convertFromWallet?.currency?.code}`}</Text>
              </Box>
            </Box>
          </Box>
          <Box flexCol paddingT={4} gapY={8} width="full">
            <Box flex justify="between" gapX={16}>
              <Text color="subdued">{t('wallets.convert.fee')}</Text>
              <Box className="text-end">
                <Text variant="body-label">{`${quote?.fee} ${quote?.from}`}</Text>
              </Box>
            </Box>
          </Box>
          <Box flexCol paddingT={4} gapY={8} width="full">
            <Box flex justify="between" gapX={16}>
              <Text color="subdued">{t('wallets.convert.total')}</Text>
              <Box className="text-end">
                <Text variant="body-label">{`${numberWithCommas(
                  floorWithPrecision(quote?.amountDue, convertFromWallet?.currency?.pricePrecision)
                )} ${convertFromWallet?.currency?.code}`}</Text>
              </Box>
            </Box>
          </Box>
          <Box flexCol paddingT={4} gapY={8} width="full">
            <Box flex justify="between" gapX={16}>
              <Text color="subdued">{t('wallets.convert.payWith')}</Text>
              <Box className="text-end">
                <Text variant="body-label">{convertFromWallet?.currency?.code}</Text>
              </Box>
            </Box>
          </Box>
          <Box flexCol paddingT={4} gapY={8} width="full">
            <Box flex justify="between" gapX={16}>
              <Text color="subdued">{t('wallets.convert.quoteExpiresIn')}</Text>
              <Box className="text-end">
                <Text variant="body-label">
                  {isRefreshingQuote ? (
                    <Box flex gapX={4}>
                      <Button noPadding variant="ghost" loading />
                      <Text color="feature">{t('requestingNewQuote')}</Text>
                    </Box>
                  ) : (
                    <CountdownHighlight onExpiry={handleRefreshQuote} />
                  )}
                </Text>
              </Box>
            </Box>
          </Box>
          <Callout variant="info">
            <Text color="inherit">
              {t('wallets.convert.convertMessage', {
                toCurrency: convertToWallet?.currency?.code,
                fromCurrency: convertFromWallet?.currency?.code,
              })}
            </Text>
          </Callout>
        </DetailsContainer>
      </Card>
      <Box flex justify="between" paddingT={16}>
        <Button testId="back" variant="secondary" onClick={walletAction?.onBack}>
          {t('back')}
        </Button>

        <Button
          testId="convert-now"
          type="button"
          variant="primary"
          onClick={handleAcceptQuote}
          loading={isAcceptingQuote || isRefreshingQuote}
        >
          {t('wallets.convert.convertNow')}
        </Button>
      </Box>
    </Container>
  )
}

const CountdownHighlight = ({ onExpiry }: { onExpiry: () => void }) => {
  const quote = useAtomValue(quoteAtom)
  const totalExpiry = Math.round(
    (new Date(quote?.acceptanceExpiryDate).getTime() - new Date().getTime()) / 1000
  )
  const { formattedTime, timeLeft, resetTimer } = useMinutesSecondsTimer(
    0,
    totalExpiry,
    true,
    onExpiry
  )

  useEffect(() => {
    if (timeLeft) return

    resetTimer()
  }, [timeLeft])

  return (
    <Highlight type="warning">{`${formattedTime?.minutes}:${formattedTime?.seconds}`}</Highlight>
  )
}
